import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { TextField as FormikTextField } from "formik-mui";
import { museumApi } from "../../../../services/MuseumServices";

export default function ExhibitionFormDialog({
  open,
  onClose,
  editingItem,
  validationSchema,
  onSubmit,
}) {
  const [museums, setMuseums] = useState([]);
  const [loadingMuseums, setLoadingMuseums] = useState(false);
  const [nextMuseumPage, setNextMuseumPage] = useState(null);
  const [loadingMoreMuseums, setLoadingMoreMuseums] = useState(false);

  const getNextPageNumber = (nextPageUrl) => {
    if (!nextPageUrl) return null;
    try {
      const url = new URL(nextPageUrl);
      const p = url.searchParams.get("page");
      return p ? Number(p) : null;
    } catch {
      return null;
    }
  };

  const loadMuseums = (page = 1, append = false) => {
    return museumApi("get", null, { page })
      .then((res) => {
        const nextPage = getNextPageNumber(res?.meta?.next_page);
        setNextMuseumPage(nextPage);

        const incoming = res?.data || [];
        setMuseums((prev) => {
          if (!append) return incoming;
          const existingIds = new Set(prev.map((m) => String(m.id)));
          const merged = [...prev];
          incoming.forEach((m) => {
            if (!existingIds.has(String(m.id))) merged.push(m);
          });
          return merged;
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch museums:", err);
        if (!append) {
          setMuseums([]);
          setNextMuseumPage(null);
        }
      });
  };

  useEffect(() => {
    if (!open) return;
    setLoadingMuseums(true);
    loadMuseums(1, false)
      .finally(() => {
        setLoadingMuseums(false);
      });
  }, [open]);

  const handleLoadMoreMuseums = () => {
    if (!nextMuseumPage || loadingMoreMuseums) return;
    setLoadingMoreMuseums(true);
    loadMuseums(nextMuseumPage, true).finally(() => {
      setLoadingMoreMuseums(false);
    });
  };

  const selectedMuseum = useMemo(() => {
    if (!editingItem?.museum_id) return null;
    return museums.find((m) => String(m.id) === String(editingItem.museum_id)) || null;
  }, [editingItem?.museum_id, museums]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingItem ? "Edit Exhibition" : "Add Exhibition"}</DialogTitle>
      <Formik
        initialValues={{
          museum_id: editingItem?.museum_id || "",
          is_active: editingItem?.is_active ?? true,
          start_date: (editingItem?.start_date || "").slice(0, 10),
          end_date: (editingItem?.end_date || "").slice(0, 10),
          name: editingItem?.translations?.en?.name || editingItem?.name || "",
          description:
            editingItem?.translations?.en?.description || editingItem?.description || "",
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={museums}
                    loading={loadingMuseums}
                    value={
                      museums.find((m) => String(m.id) === String(values.museum_id)) ||
                      selectedMuseum ||
                      null
                    }
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={(_, value) => {
                      setFieldValue("museum_id", value?.id || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Museum"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingMuseums ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                {nextMuseumPage && (
                  <Grid item xs={12}>
                    <Button
                      variant="text"
                      onClick={handleLoadMoreMuseums}
                      disabled={loadingMoreMuseums}
                      sx={{ p: 0, minWidth: 0, textTransform: "none" }}
                    >
                      {loadingMoreMuseums ? "Loading..." : "See more"}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.is_active}
                        onChange={(e) => setFieldValue("is_active", e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FormikTextField}
                    name="start_date"
                    label="Start date"
                    type="date"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FormikTextField}
                    name="end_date"
                    label="End date"
                    type="date"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={FormikTextField}
                    name="name"
                    label="Name"
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={FormikTextField}
                    name="description"
                    label="Description"
                    fullWidth
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {editingItem ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
